// The week of skies (docs/03-Experience/Night Sky Construction.md): one per
// weekday, Monday lightest to Sunday deepest, each two screens tall: home at
// the bottom with the horizon, play above. Starting values, to tune. The
// animated bits per day live in the game (display/sky/ambient/ambientConfig.ts).
// Here in the core because the game's sky and the Worker's share page
// (docs/01-Product/Sharing.md) draw the same nights.

export type Weekday = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface WeekdaySky {
  top: string;
  mid: string;
  bottom: string;
  /** 0–1 horizon warmth on the home half. */
  horizonWarmth: number;
  /** 0–1 Milky Way opacity, 0 for none. */
  milkyWay: number;
}

export const WEEKDAY_SKIES: Record<Weekday, WeekdaySky> = {
  monday: { top: '#0E2238', mid: '#1E3A58', bottom: '#3A5C7E', horizonWarmth: 0.55, milkyWay: 0 },
  tuesday: { top: '#0B1D2E', mid: '#193350', bottom: '#2E4D6B', horizonWarmth: 0.38, milkyWay: 0 },
  wednesday: { top: '#09182A', mid: '#142C47', bottom: '#24405C', horizonWarmth: 0.24, milkyWay: 0 },
  thursday: { top: '#07131F', mid: '#10263D', bottom: '#1C344D', horizonWarmth: 0.12, milkyWay: 0.05 },
  friday: { top: '#06101A', mid: '#0D2036', bottom: '#15304A', horizonWarmth: 0, milkyWay: 0 },
  saturday: { top: '#050C15', mid: '#0A1B2E', bottom: '#10263D', horizonWarmth: 0, milkyWay: 0.1 },
  sunday: { top: '#04080E', mid: '#071526', bottom: '#0B1D2E', horizonWarmth: 0, milkyWay: 0.13 },
};

export const WEEKDAY_ORDER: Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function weekdayFromDate(date: string): Weekday {
  const days: Weekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date(`${date}T00:00:00Z`).getUTCDay()]!;
}

/**
 * The background field in sky units (1000 is one screen height, as on the
 * board): two screens tall, play above and home below, and wide enough to
 * wrap sideways without the repeat showing. It is drawn in tiles so only the
 * part near the view is in the page.
 */
export const FIELD_WIDTH = 6000;
export const FIELD_HEIGHT = 2000;
export const HALF_HEIGHT = 1000;
export const TILE_WIDTH = 1000;
export const TILE_COUNT = FIELD_WIDTH / TILE_WIDTH;

/** mulberry32: a small seeded generator so every player sees the same background. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type DepthLayer = 0 | 1 | 2;

export interface BackgroundStar {
  id: number;
  /** Position in the field, sky units. */
  x: number;
  y: number;
  /** Radius in screen pixels on a desktop window; the game draws them smaller on smaller screens (viewport.ts, starSize). */
  r: number;
  opacity: number;
  cool: boolean;
  layer: DepthLayer;
}

/** Far, mid and near stars over the whole field, at the density of the round-three sky studies. */
export function backgroundStars(weekday: Weekday): BackgroundStar[] {
  const rand = seededRandom(WEEKDAY_ORDER.indexOf(weekday) * 7919 + 17);
  const stars: BackgroundStar[] = [];
  const perScreenPair = FIELD_WIDTH / 2000;
  const layers: Array<{ count: number; r: [number, number]; o: [number, number]; layer: DepthLayer }> = [
    { count: 260, r: [0.7, 1.4], o: [0.14, 0.44], layer: 0 },
    { count: 120, r: [1.4, 2.2], o: [0.35, 0.7], layer: 1 },
    { count: 48, r: [2.4, 3.6], o: [0.6, 0.95], layer: 2 },
  ];
  for (const l of layers) {
    for (let i = 0; i < l.count * perScreenPair; i++) {
      stars.push({
        id: stars.length,
        x: rand() * FIELD_WIDTH,
        y: rand() * FIELD_HEIGHT,
        r: l.r[0] + rand() * (l.r[1] - l.r[0]),
        opacity: l.o[0] + rand() * (l.o[1] - l.o[0]),
        cool: rand() < 0.25,
        layer: l.layer,
      });
    }
  }
  return stars;
}

/** The stars of one depth layer bucketed by tile, left to right. */
export function starTiles(stars: BackgroundStar[], layer: DepthLayer): BackgroundStar[][] {
  const tiles: BackgroundStar[][] = Array.from({ length: TILE_COUNT }, () => []);
  for (const s of stars) {
    if (s.layer === layer) tiles[Math.min(TILE_COUNT - 1, Math.floor(s.x / TILE_WIDTH))]!.push(s);
  }
  return tiles;
}
